import { simpleObjectHash } from '@/features/shared/tools/object'

export function withCache<TArgs extends readonly unknown[], TReturn>(
  func: (...args: TArgs) => TReturn,
  options?: { cacheSize: number },
): typeof func {
  type CacheItem = {
    key: string
    value: TReturn
  }
  const cache: CacheItem[] = []

  return (...args: TArgs) => {
    // Calcule une clée pour les paramétres
    const key = simpleObjectHash(args)

    // Recherche le résultat
    let result = cache.find((item) => item.key === key)
    if (result) return result.value

    // Execute func si absent
    result = {
      key,
      value: func(...args),
    }

    //Ajoute au cache en première position
    cache.unshift(result)

    // Si nb éléments > cacheSize, retire le dernier élément
    if (options?.cacheSize && cache.length > options.cacheSize) {
      cache.pop()
    }

    return result.value
  }
}

/**
 * Exécute une fonction de façon sécurisée (sans lever d'exception).
 *
 * - Si `fn` est `undefined`, la fonction ne fait rien et retourne `undefined`.
 * - Si `fn` s'exécute correctement, son résultat est retourné.
 * - Si `fn` lève une exception, l'erreur est interceptée, journalisée via `console.error`,
 *   puis la fonction retourne `undefined`.
 *
 * @typeParam T - Type de la valeur retournée par `fn`.
 * @param fn - Fonction à exécuter (optionnelle).
 * @returns Le résultat de `fn` si tout se passe bien, sinon `undefined`.
 *
 * @example
 * const value = safeCall(() => JSON.parse('{"ok": true}'))
 * // value -> { ok: true }
 *
 * @example
 * const value = safeCall(() => JSON.parse('invalid-json'))
 * // console.error(...)
 * // value -> undefined
 */
export function safeCall<T>(fn: (() => T) | undefined): T | undefined {
  try {
    return fn?.()
  } catch (error) {
    console.error(error)
  }
}

export function isFunction(value: unknown): value is (...args: any[]) => any {
  return typeof value === 'function'
}

/**
 * Réalise une application partielle des arguments d'une fonction.
 *
 * Cette fonction prend une fonction `fn` et un ensemble d'arguments initiaux `args1`.
 * Elle retourne une nouvelle fonction qui, lorsqu'elle est appelée avec les arguments restants `args2`,
 * exécute `fn` avec la concaténation de `args1` et `args2`.
 *
 * @typeParam T - Type des arguments fournis initialement.
 * @typeParam U - Type des arguments restants à fournir.
 * @typeParam R - Type de la valeur de retour de la fonction.
 *
 * @param fn - La fonction à laquelle appliquer partiellement les arguments.
 * @param args1 - Les premiers arguments à fixer.
 * @returns Une nouvelle fonction attendant les arguments restants.
 *
 * @example
 * const add = (a: number, b: number) => a + b
 * const addFive = compose(add, 5)
 * const result = addFive(10) // result -> 15
 */
export function compose<T extends any[], U extends any[], R>(
  fn: (...args: [...T, ...U]) => R,
  ...args1: T
): (...args2: U) => R {
  return (...args2: U) => fn(...args1, ...args2)
}
